import React, { useEffect, useMemo, useRef, useState } from "react";
import { Spin, Select, DatePicker, Button, message, Tooltip } from "antd";
import "./index.scss";
import {
  getApcOrderList,
  getBpcOrderList,
  getPpvOrderList,
  getMeetingOrderList,
  openCertificate,
  getPtpOrderList,
} from "../../../api/personal";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { SvgIcon } from "@/components/SvgIcon";
const { RangePicker } = DatePicker;
import { IS_MOBILE, goHref } from "@/utils/tool";
import InfiniteScroll from "react-infinite-scroll-component";
import { fetchAttorney } from "@/api/common";
import {
  SOURCE_ORDER_TYPE,
  OrderStatus,
  PUBLISHER_SOURCE_ORDER_TYPE_NUM,
} from "@/utils/contants";
import PodConfirm from "./components/PodConfirm";

const OrderStatusString = {
  1: "未支付",
  2: "已支付",
};

const OrderRefundStatusString = {
  2: "退款中",
  3: "已退款",
};

enum RefundStatus {
  // 未退款
  No_refund = 1,
  // 退款中
  RefundIng = 2,
  // 退款完成
  Done = 3,
}

type OrderProps = {
  orderType: SOURCE_ORDER_TYPE;
  scrollableTargetId: string;
};

const orderHttpApi = {
  [SOURCE_ORDER_TYPE.Apc]: getApcOrderList,
  [SOURCE_ORDER_TYPE.Meeting]: getMeetingOrderList,
  [SOURCE_ORDER_TYPE.Bpc]: getBpcOrderList,
  [SOURCE_ORDER_TYPE.Ppv]: getPpvOrderList,
  [SOURCE_ORDER_TYPE.Ptp]: getPtpOrderList,
};

const Order: React.FC<OrderProps> = ({ orderType, scrollableTargetId }) => {
  const { t } = useTranslation();
  const [pageNumber, setPageNumber] = useState(1);
  const [orderList, setOrderList] = useState<PersonalOrder[]>([]);
  const [orderCount, setOrderCount] = useState(0);
  const [spinning, setSpinning] = useState(true);
  const [times, setTimes] = useState([]);
  const [payStatus, setPayStatus] = useState(0);
  const [btnLoading, setBtnLoading] = useState("");
  const [messageApi, contextHolder] = message.useMessage();
  const isResetSearch = useRef(false);
  const [curApcOrder, setCurApcOrder] = useState<PersonalOrder>();

  const fetchOrderList = async (params, type: "scroll" | "new") => {
    setSpinning(true);
    const orderRes = await orderHttpApi[orderType]<{
      count: number;
      list: Array<PersonalOrder>;
      n: number;
      p: number;
    }>(params);
    setSpinning(false);
    if (orderRes.code === 0) {
      const { count, list } = orderRes.data;
      setOrderCount(count);
      if (type === "scroll") {
        setOrderList((pre) => [...pre, ...list]);
      } else {
        setOrderList(list);
      }
      return;
    }
    messageApi.error(orderRes.msg);
  };
  useEffect(() => {
    fetchOrderList(
      {
        pay_status: payStatus,
        pay_time_min: times[0]?.startOf("day").unix(),
        refund_status: times[1]?.add(1, "day").startOf("day").unix(),
        p: 1,
        n: 10,
      },
      "new",
    );
  }, [orderType]);

  useEffect(() => {
    pageNumber > 1 &&
      fetchOrderList(
        {
          pay_status: payStatus,
          pay_time_min: times[0]?.startOf("day").unix(),
          refund_status: times[1]?.add(1, "day").startOf("day").unix(),
          p: pageNumber,
          n: 10,
        },
        "scroll",
      );
  }, [pageNumber]);

  /**
   * 时间发生改变时
   * @param times
   */
  const onChangeTime = (times) => {
    if (!times) {
      setTimes([]);
      return;
    }
    if (times[0] && times[1]) {
      setTimes(times);
    }
  };

  const submitSearch = () => {
    const params = {
      pay_status: payStatus,
      pay_time_min: times[0]?.startOf("day").unix(),
      pay_time_max: times[1]?.add(1, "day").startOf("day").unix(),
      p: 1,
      n: 10,
    };
    // 重置列表与页码
    setPageNumber(1);
    setOrderList([]);
    fetchOrderList(params, "new");
  };

  const resetSearch = () => {
    setPayStatus(0);
    setTimes([]);
    setPageNumber(1);
    setOrderList([]);
    fetchOrderList(
      {
        p: 1,
        n: 10,
      },
      "new",
    );
  };

  const toPay = (url) => {
    window.location.href = url;
  };

  /**
   * 开证明
   */
  const goCertificate = async (order: PersonalOrder) => {
    setBtnLoading(order.order_id);
    const res = await openCertificate<{ url: string }>(order.order_id);
    setBtnLoading("");
    if (res.code === 0) {
      const url = res.data.url;
      goHref(url);
      return;
    }
    messageApi.error(res.msg);
  };

  /**
   * 下载出版社授权书
   */
  const downloadPowerOfAttorney = async (id: string) => {
    fetchAttorney({ order_ids: [id] }).then((res) => {
      if (res.code === 0) {
        const url = res.data[0].power_of_attorney;
        goHref(url);
      } else {
        message.error(res.msg);
      }
    });
  };

  /**
   * 定购当前期刊
   * @param order
   */
  const podConfirmHandel = (order: PersonalOrder) => {
    setCurApcOrder(order);
  };

  /**
   * 关闭定购当期期刊
   */
  const closePodConfirm = () => {
    setCurApcOrder(undefined);
  };

  useEffect(() => {
    if (IS_MOBILE) {
      const rangePickerDom = document.getElementById("range-picker-container");
      if (rangePickerDom) {
        let inputElements = rangePickerDom.getElementsByTagName("input");
        for (var i = 0; i < inputElements.length; i++) {
          inputElements[i].readOnly = true;
        }
      }
    }
  }, []);

  return (
    <>
      {contextHolder}
      <section className="content-wrapper">
        <section className="search-wrapper">
          <div className="search">
            <div className="item">
              <span className="title">{t("支付状态")}</span>
              <Select
                value={payStatus}
                options={[
                  { value: 0, label: `${t("全部")}` },
                  { value: 1, label: `${t("未支付")}` },
                  { value: 2, label: `${t("已支付")}` },
                ]}
                style={{ width: 150 }}
                onChange={(value) => setPayStatus(value)}
              />
            </div>
            <div className="item" id="range-picker-container">
              <span
                className="title"
                style={
                  IS_MOBILE ? { display: "inline-block", marginBottom: 8 } : {}
                }
              >
                {t("支付时间")}
              </span>
              <RangePicker
                style={{ width: 300 }}
                format="YYYY-MM-DD"
                onChange={onChangeTime}
                value={times as any}
                popupClassName={`${IS_MOBILE ? "mobile-range-picker" : ""}`}
                getPopupContainer={(node) => node.parentElement ?? node}
              />
            </div>
          </div>
          <div className="btn">
            <Button onClick={resetSearch}>{t("重置")}</Button>
            <Button type="primary" onClick={submitSearch}>
              {t("查询")}
            </Button>
          </div>
        </section>
        {orderList.length !== 0 ? (
          <section className="order-list-wrapper" id={scrollableTargetId}>
            <>
              <InfiniteScroll
                dataLength={orderList.length}
                next={() => setPageNumber(pageNumber + 1)}
                hasMore={true}
                loader={null}
                scrollableTarget={scrollableTargetId}
              >
                {orderList.map((item, index) => (
                  <div className="order-item" key={index}>
                    <div className="number">
                      <div>
                        <span>{t("支付号")}:</span>
                        {item.order_id}
                      </div>
                      {item.refund_status === 2 || item.refund_status === 3 ? (
                        <div>
                          {t(OrderRefundStatusString[item.refund_status])}
                        </div>
                      ) : (
                        <div>{t(OrderStatusString[item.pay_status])}</div>
                      )}
                    </div>
                    <section className="info-wrapper">
                      {[
                        PUBLISHER_SOURCE_ORDER_TYPE_NUM.Apc,
                        PUBLISHER_SOURCE_ORDER_TYPE_NUM.Ppv,
                        PUBLISHER_SOURCE_ORDER_TYPE_NUM.Ptp,
                      ].includes(item.publishing_house_order_type) && (
                        <>
                          <div className="periodicals">
                            <span className="title">{t("期刊名称")}:</span>
                            <Tooltip
                              placement="top"
                              title={item.journal_name}
                              className="name"
                              overlayStyle={{
                                fontSize: "1rem",
                                maxWidth: "500px",
                              }}
                              color="rgba(39, 46, 59, 0.9)"
                            >
                              <span>{item.journal_name}</span>
                            </Tooltip>
                          </div>
                          <div className="article-title">
                            <span className="title">{t("文章标题")}:</span>
                            <Tooltip
                              placement="top"
                              title={item.article_title}
                              className="name"
                              overlayStyle={{
                                fontSize: "1rem",
                                maxWidth: "500px",
                              }}
                              color="rgba(39, 46, 59, 0.9)"
                            >
                              <span className="title-info">
                                {item.article_title}
                              </span>
                            </Tooltip>
                          </div>
                        </>
                      )}
                      {item.publishing_house_order_type ==
                        PUBLISHER_SOURCE_ORDER_TYPE_NUM.Meeting && (
                        <>
                          <div className="periodicals">
                            <span className="title">{t("会议名称")}:</span>
                            <Tooltip
                              placement="top"
                              title={item.meeting_name}
                              className="name"
                              overlayStyle={{
                                fontSize: "1rem",
                                maxWidth: "500px",
                              }}
                              color="rgba(39, 46, 59, 0.9)"
                            >
                              <span className="title-info">
                                {item.meeting_name}
                              </span>
                            </Tooltip>
                          </div>
                          <div className="article-title">
                            <span className="title">{t("会议开始时间")}:</span>
                            {item.meeting_start_time
                              ? dayjs
                                  .unix(item.meeting_start_time)
                                  .format("YYYY-MM-DD")
                              : "-"}
                          </div>
                          <div className="article-title">
                            <span className="title">{t("会议结束时间")}:</span>
                            {item.meeting_end_time
                              ? dayjs
                                  .unix(item.meeting_end_time)
                                  .format("YYYY-MM-DD")
                              : "-"}
                          </div>
                        </>
                      )}
                      {item.publishing_house_order_type ==
                        PUBLISHER_SOURCE_ORDER_TYPE_NUM.Bpc && (
                        <>
                          <div className="periodicals">
                            <span className="title">{t("订单号")}:</span>
                            {item.publishing_house_order_id}
                          </div>
                          <div className="periodicals">
                            <span className="title">{t("出版社")}:</span>
                            {item.publishing_house_name}
                          </div>
                          <div className="periodicals">
                            <span className="title">{t("图书标题")}:</span>
                            <Tooltip
                              placement="top"
                              title={item.book_title}
                              className="name"
                              overlayStyle={{
                                fontSize: "1rem",
                                maxWidth: "500px",
                              }}
                              color="rgba(39, 46, 59, 0.9)"
                            >
                              <span className="title-info">
                                {item.book_title}
                              </span>
                            </Tooltip>
                          </div>
                        </>
                      )}
                      {/* ptp信息 */}
                      {item.publishing_house_order_type ==
                        PUBLISHER_SOURCE_ORDER_TYPE_NUM.Ptp && (
                        <div className="periodicals">
                          <span className="title">PTP:</span>
                          {item.ptp_detail}
                        </div>
                      )}
                      <div className="pay-time">
                        <span className="title">{t("支付时间")}:</span>
                        {item.pay_time
                          ? dayjs(item.pay_time * 1000).format(
                              "YYYY-MM-DD HH:mm:ss",
                            )
                          : "-"}
                      </div>
                      <div className="pay-amount">
                        <div className="money">
                          <span>{t("支付金额")}:</span>
                          <span>¥ {item.total_money.toFixed(2)}</span>
                        </div>
                        {item.pay_status === 1 && (
                          <Button
                            className="btn-primary"
                            onClick={() => toPay(item.pay_url)}
                          >
                            {t("去支付")}
                          </Button>
                        )}
                        {item.has_power_of_attorney && (
                          <Button
                            onClick={() =>
                              downloadPowerOfAttorney(item.order_id)
                            }
                            className="hold power-of-attorney"
                          >
                            {t("下载出版社授权书")}
                          </Button>
                        )}
                        {item.publishing_house_order_type ==
                          PUBLISHER_SOURCE_ORDER_TYPE_NUM.Apc &&
                          item.pay_status === 2 &&
                          item.refund_status === RefundStatus.No_refund && (
                            <Button
                              loading={btnLoading === item.order_id}
                              onClick={() => goCertificate(item)}
                              className="hold certificate"
                            >
                              {t("开具证明")}
                            </Button>
                          )}
                        {item.publishing_house_order_type ==
                          SOURCE_ORDER_TYPE.Apc &&
                          item.enable_pod_order &&
                          item.pay_status === OrderStatus.Paid &&
                          item.refund_status === RefundStatus.No_refund && (
                            <Button
                              type="primary"
                              onClick={() => podConfirmHandel(item)}
                              className="hold certificate"
                            >
                              {t("定购当期纸本期刊")}
                            </Button>
                          )}
                      </div>
                    </section>
                  </div>
                ))}
              </InfiniteScroll>
              {spinning && (
                <div className="spin-wrapper">
                  <Spin size="large" spinning={spinning} />
                </div>
              )}
            </>
          </section>
        ) : (
          <div className="empty-content" style={{ marginTop: "1.583rem" }}>
            <SvgIcon
              icon={
                payStatus || times.length ? "icon-wujieguo" : "icon-wuliebiao"
              }
            />
            {payStatus || times.length ? t("暂无相匹配的订单") : t("暂无订单")}
          </div>
        )}
      </section>
      {curApcOrder && (
        <PodConfirm order={curApcOrder} cancelModal={closePodConfirm} />
      )}
    </>
  );
};

export default Order;
